import { useServices } from '@/hooks/use-services';
import { ArrowLeft, ChevronRight, Info } from 'lucide-react';
import { useRouter } from '@/lib/router-helpers';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { useBookingStore } from "@/store/booking-store"

interface ServiceListProps {
  selectedBranch: {id: string; name: string; subtitle: string };
  onBack: () => void;
}

const containerVariants = {
  hidden: { opacity: 0 },
  show: {
    opacity: 1,
    transition: {
      staggerChildren: 0.05
    }
  }
};

const itemVariants = {
  hidden: { opacity: 0, x: -20 },
  show: { opacity: 1, x: 0, transition: { type: "spring", stiffness: 300, damping: 24 } }
} as const;

export default function ServiceList({ selectedBranch, onBack }: ServiceListProps) {
  const router = useRouter();
  const { t } = useTranslation();
  const setBookingState = useBookingStore((state) => state.setBookingState)

  const { data: services = [], isLoading, error } = useServices(selectedBranch.id);

  function iconForService(name: string): string {
    const key = name.toLowerCase();
    if (key.includes("representation")) return "/icon/representation.svg";
    if (key.includes("rental")) return "/icon/rental.svg";
    if (key.includes("sales")) return "/icon/sales.svg";
    if (key.includes("plc")) return "/icon/plc.svg";
    if (key.includes("gift")) return "/icon/gift.svg";
    if (key.includes("loan")) return "/icon/loan.svg";
    return "/icon/other.svg";
  }

  function descriptionForService(name: string): string {
    const key = name.toLowerCase();
    if (key.includes("representation")) return "Representation related service";
    if (key.includes("rental")) return "Rental related service";
    if (key.includes("sales")) return "Sales related service";
    if (key.includes("plc")) return "PLC related service";
    if (key.includes("gift")) return "Gift related service";
    if (key.includes("loan")) return "Loan related service";
    return "Other related service";
  }

  function handleClick(serviceId: string, serviceName: string) {
    setBookingState({ 
      selectedBranch,
      selectedService: { id: serviceId, name: serviceName },
      selectedSlot: null, // Clear slot when service changes
      selectedDate: null // Clear date when service changes
    })
    router.push(`/booking/schedule`);
  }

  if (isLoading) {
    return (
      <div className="px-4 pt-3 pb-6 space-y-4">
        <div className="flex items-center gap-3 mb-4">
           <div className="w-9 h-9 rounded-full bg-gray-100 animate-pulse" />
           <div className="h-5 w-32 bg-gray-100 rounded animate-pulse" />
        </div>
        {[1, 2, 3, 4].map((i) => (
          <div key={i} className="flex items-center gap-4 p-4 border-b border-gray-100">
            <div className="w-12 h-12 rounded-full bg-gray-100 animate-pulse" />
            <div className="flex-1 space-y-2">
              <div className="h-4 w-3/4 bg-gray-100 rounded animate-pulse" />
              <div className="h-3 w-1/2 bg-gray-100 rounded animate-pulse" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex flex-col items-center justify-center h-64 px-4 text-center">
        <div className="w-12 h-12 bg-red-50 rounded-full flex items-center justify-center mb-3">
          <Info className="w-6 h-6 text-red-500" />
        </div>
        <p className="text-gray-900 font-medium">{t("Failed to load services")}</p>
        <button onClick={onBack} className="mt-4 text-pink-600 font-medium hover:underline">
          {t("Go back")}
        </button>
      </div>
    );
  }

  return (
    <div className="px-4 pt-3 pb-6">
      {/* Header */}
      <div className="flex items-center gap-3 mb-6 sticky top-0 bg-white z-10 py-2">
        <motion.button
          whileHover={{ scale: 1.1 }}
          whileTap={{ scale: 0.9 }}
          onClick={onBack}
          aria-label="Back"
          className="w-9 h-9 rounded-full bg-white border border-gray-100 flex items-center justify-center shadow-sm hover:bg-gray-50 transition-colors"
        >
          <ArrowLeft className="w-5 h-5 text-pink-600" />
        </motion.button>
        <div>
          <h2 className="text-lg font-bold text-gray-900">{t("selectService")}</h2>
          <p className="text-xs text-gray-500">{t(selectedBranch.name)}</p>
        </div>
      </div>

      {/* List */}
      <motion.div 
        variants={containerVariants}
        initial="hidden"
        animate="show"
        className="bg-white rounded-2xl overflow-hidden border border-gray-100 shadow-sm"
      >
        {services.length === 0 ? (
           <div className="p-8 text-center text-gray-500">
             {t("No services available for this branch.")}
           </div>
        ) : (
          services.map((s, i) => (
            <motion.div
              key={s.id}
              variants={itemVariants}
              role="button"
              tabIndex={0}
              onClick={() => handleClick(s.id, s.name)}
              onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') handleClick(s.id, s.name) }}
              className="group flex items-center gap-3 px-4 py-3 border-b border-gray-100 last:border-0 hover:bg-pink-50/50 cursor-pointer transition-colors relative overflow-hidden"
            >
              {/* Hover highlight bar */}
              <div className="absolute left-0 top-0 bottom-0 w-1 bg-pink-500 opacity-0 group-hover:opacity-100 transition-opacity" />

              <div className='flex flex-col flex-1'>
                <div className='flex items-center gap-3'>
                  <div className="w-10 h-10 flex-shrink-0 bg-gray-50 rounded-xl flex items-center justify-center group-hover:scale-110 transition-transform duration-300">
                    <img src={iconForService(s.name)} alt={s.name} className="w-6 h-6" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center justify-between">
                      <div className="text-sm font-semibold text-gray-900 truncate group-hover:text-pink-700 transition-colors">{t(s.name)}</div>
                    </div>
                
                  </div>
                </div>
                  {s.special_notes &&(
                  <div className="inline-flex items-center gap-1.5 mt-1.5 px-2.5 py-1 rounded-lg bg-[#FFFBEB] border border-[#FEF3C7] w-fit max-w-full">
                    <Info className="w-3.5 h-3.5 flex-shrink-0 text-[#F59E0B]" />
                    <span className="text-xs font-medium truncate text-[#92400E]">{t(s.special_notes)}</span>
                  </div>
                )}
              </div>

              <div className="flex items-center">
                <ChevronRight className="w-4 h-4 text-gray-300 group-hover:text-pink-500 group-hover:translate-x-1 transition-all" />
              </div>
            </motion.div>
          ))
        )}
      </motion.div>
    </div>
  );
}
